import React, { useState, useCallback, useEffect } from "react";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { ReactFlowProvider } from "@xyflow/react";
import { Box, Button, Typography, Paper, Chip, Stack } from "@mui/material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Flow, FiliationPanel, TimeLinePanel } from "@/diagramme/components";

import { styled } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";

import Logo from "@/taskpane/components/logo/Logo";

import { useNavigate, useParams } from "react-router-dom";
import { usePrevious } from "@uidotdev/usehooks";
import { useGetPublicFiliationQuery } from "@/taskpane/services/filiation.hook";

import {
  BLUE_BYLAW,
  BORDER_RADIUS_MUI,
  DEFAULT_BLUR,
  DEFAULT_ELEVATION,
  DEFAULT_PADDING,
  LOGO_BOX_WIDTH,
  LOGO_WIDTH,
  PANEL_HEADER_TITLE_HEIGHT,
  PANEL_WIDTH,
  TOOLBAR_HEIGHT,
  WHITE,
} from "@/diagramme/config";

import { areArraysEquals, isEqual, getFiliationEventsGroupedByDates } from "@/diagramme/utils";

import type {
  FiliationEvent,
  FiliationNode,
  FiliationTree,
  FiliationEventWithFiliationStateGroupedByDate,
} from "@/diagramme/types";

dayjs.extend(customParseFormat);

const drawerWidth = PANEL_WIDTH;

// Composant Légende pour la vue d'ensemble
const FiliationLegend = ({ drawerOpen }: { drawerOpen: boolean }) => (
  <Paper
    elevation={2}
    sx={{
      position: "absolute",
      top: 120,
      left: drawerOpen ? PANEL_WIDTH + DEFAULT_PADDING + 20 : 20,
      zIndex: 1000,
      p: 2,
      borderRadius: 2,
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      transition: "left 0.3s ease",
    }}
  >
    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
      Légende
    </Typography>
    <Stack spacing={1.5}>
      {/* Commune */}
      <Chip
        label="Commune"
        size="small"
        sx={{
          backgroundColor: "#9e9e9e",
          color: "#fff",
          fontWeight: 500,
          width: "fit-content",
        }}
      />
      {/* Parcelles */}
      <Stack direction="row" spacing={1}>
        <Chip
          label="Parcelle existante"
          size="small"
          sx={{
            backgroundColor: "#4caf50",
            color: "#fff",
            fontWeight: 500,
          }}
        />
        <Chip
          label="Parcelle supprimée"
          size="small"
          sx={{
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            border: "1px solid #81c784",
            fontWeight: 500,
          }}
        />
      </Stack>
      {/* Volumes */}
      <Stack direction="row" spacing={1}>
        <Chip
          label="Volume existant"
          size="small"
          sx={{
            backgroundColor: "#f57c00",
            color: "#fff",
            fontWeight: 500,
          }}
        />
        <Chip
          label="Volume supprimé"
          size="small"
          sx={{
            backgroundColor: "#ffe0b2",
            color: "#e65100",
            border: "1px solid #ffb74d",
            fontWeight: 500,
          }}
        />
      </Stack>
      {/* Lots */}
      <Stack direction="row" spacing={1}>
        <Chip
          label="Lot existant"
          size="small"
          sx={{
            backgroundColor: "#1565c0",
            color: "#fff",
            fontWeight: 500,
          }}
        />
        <Chip
          label="Lot supprimé"
          size="small"
          sx={{
            backgroundColor: "#bbdefb",
            color: "#1565c0",
            border: "1px solid #64b5f6",
            fontWeight: 500,
          }}
        />
      </Stack>
    </Stack>
  </Paper>
);

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(0),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
      },
    },
  ],
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        width: `100%`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const FiliationDiagramme = () => {
  /**
   * @description Extract route parameters
   */
  const { client_id, dossier_id, sous_dossier_id, flow_id } = useParams();

  /**
   * @description Fetch filiation data using a custom hook. This method call a public endpoint.
   * @param {string} client_id - The client Id or Etude
   * @param {string} dossier_id - The dossier ID
   * @param {string} sous_dossier_id - The sous-dossier ID
   * @param {string} flow_id - The flow ID
   * @returns {Object} data - The fetched filiation data
   */
  const { data, isLoading }: any = useGetPublicFiliationQuery(
    client_id || "",
    dossier_id || "",
    sous_dossier_id || "",
    flow_id || "",
    {
      initialData: undefined,
    }
  );

  /**
   * @description Hook to keep track of the previous state of data.
   * This allow to react to actual changes of data.
   */
  const previousData = usePrevious(data);

  /**
   * @description State for storing the chosen filiation. By chosen we mean here, filiation data from the result of the user previous choice.
   * This state include all filiation object with chosen to true and their related filiation elements as their ascendants.
   */
  const [chosenFiliationData, setChosenFiliationData] = useState<any>(null);
  const previousChosenFiliationData = usePrevious(chosenFiliationData);

  /**
   * @description State storing origines filiation objects for only chosen objects.
   * The main goal of this state is to represent the differents events in time that concern the chosen filiation objects.
   */
  const [chosenFiliationEvents, setChosenFiliationEvents] = useState<FiliationEvent[] | null>(null);
  const previousChosenFiliationEvents = usePrevious(chosenFiliationEvents);

  /**
   * @description State storing a list of every filiation state for each events related to chosen filiation objects.
   */
  const [filiationStatesByEvents, setFiliationStatesByEvents] = useState<
    FiliationEventWithFiliationStateGroupedByDate[] | null
  >(null);

  /**
   * @description State holding today's date
   */
  const [today, setToday] = useState<string>(() => dayjs().format("DD/MM/YYYY"));

  /**
   * @description State storing the current date at which the filiation object need to be rendered
   */
  const [currentDate, setCurrentDate] = useState<string>(() => dayjs().format("DD/MM/YYYY"));
  const previousCurrentDate = usePrevious(currentDate);

  /**
   * @description State for managing filiation objects that have chosen to true as defaults values and keep track of manually choose elements by user.
   */
  const [selectedProperties, setSelectedProperties] = useState<number[]>([]);

  /**
   * @description Filiation panel open/close state
   */
  const [open, setOpen] = React.useState(false);

  const navigate = useNavigate();

  /**
   * @description Effect reacting on data changes to retrieve origines of chosen filiation objects and computing all
   * possible filiation states for each event found in origines related to chosen elements.
   */
  useEffect(() => {
    if (!isEqual(previousData, data) && data) {
      // Get events from filiation data origines property
      const filiationEvents = data?.origines;

      // Vérifier que les données existent avant de les traiter
      if (!filiationEvents || !data?.origines_by_date) {
        return;
      }

      // origines_by_date is the source of truth for the timeline states.
      // event_descripion is optional metadata and is filtered when the states are grouped.
      setChosenFiliationEvents([...filiationEvents]);
      // Find the most recent date in origine_by_date compare to today and set curent date to this date
      const originDates = Object.entries(data.origines_by_date)
        .filter(([, state]: [string, any]) => Array.isArray(state?.nodes) && state.nodes.length > 0)
        .map(([date]) => date);
      if (originDates.length > 0) {
        const mostRecentDate = originDates.sort((a, b) => {
          return dayjs(b, "DD/MM/YYYY").diff(dayjs(a, "DD/MM/YYYY"));
        })[0];
        setCurrentDate(dayjs(mostRecentDate, "DD/MM/YYYY").format("DD/MM/YYYY"));
      }
    }
  }, [data]);

  useEffect(() => {
    if (data && !isEqual(previousData, data)) {
      const dataNodes = data?.nodes;

      // Vérifier que les données nécessaires existent
      if (!dataNodes || !data?.origines || !data?.origines_by_date) {
        return;
      }

      const chosenNodes: any[] = [];

      const parcelles = dataNodes?.filter((node: FiliationNode) => node.type === "cadastre");
      const volumes = dataNodes?.filter((node: FiliationNode) => node.type === "volume");
      const lots = dataNodes?.filter((node: FiliationNode) => node.type === "lot");

      const chosenLots = lots.filter((lot: any) => lot.chosen === true);
      const chosenVolumes = volumes.filter((volume: any) => volume.chosen === true);
      const chosenParcelles = parcelles.filter((parcelle: any) => parcelle.chosen === true);

      chosenNodes.push(...chosenLots);
      chosenNodes.push(...chosenVolumes);
      chosenNodes.push(...chosenParcelles);

      const chosenNodesAndTheirAscendants = [...chosenNodes];

      chosenNodes.forEach((node: any) => {
        const potentialAlreadyChosenAscendantNode = chosenNodes.find((chosenNode: any) => {
          return node.parents_ids?.includes(chosenNode.id);
        });

        if (potentialAlreadyChosenAscendantNode) {
          return;
        } else {
          const ascendantNodes = dataNodes.filter((potentialAscendantNode: any) => {
            return node.parents_ids?.includes(potentialAscendantNode.id);
          });

          chosenNodesAndTheirAscendants.push(...ascendantNodes);
        }
      });

      let isAllAscendantsAddedToChosenNodes = false;

      while (!isAllAscendantsAddedToChosenNodes) {
        const ascendantsOfAllChosenNodes = chosenNodesAndTheirAscendants
          .map((node: any) => node.parents_ids)
          .flat(Infinity);
        const uniqueAscendantsOfAllChosenNodes = ascendantsOfAllChosenNodes.filter(
          (value, index, self) => self.indexOf(value) === index
        );

        const ascendantsOfAllChosenNodesThatAreNotInChosenNodes = uniqueAscendantsOfAllChosenNodes.filter(
          (ascendantId: any) => {
            return !chosenNodesAndTheirAscendants.find((node: any) => node.id === ascendantId);
          }
        );

        if (ascendantsOfAllChosenNodesThatAreNotInChosenNodes.length === 0) {
          isAllAscendantsAddedToChosenNodes = true;
        } else {
          const ascendantNodes = dataNodes.filter((potentialAscendantNode: any) => {
            return ascendantsOfAllChosenNodesThatAreNotInChosenNodes.includes(potentialAscendantNode.id);
          });

          chosenNodesAndTheirAscendants.push(...ascendantNodes);
        }
      }

      const chosenNodeIds = new Set(chosenNodesAndTheirAscendants.map(node => node.id));
      chosenNodesAndTheirAscendants.forEach(node => {
        node.children_ids = node.children_ids.filter((id: number) => chosenNodeIds.has(id));
      });

      const chosenNodesFiliationLinkedData: any = {
        nodes: [...chosenNodesAndTheirAscendants],
        origines: [...data?.origines],
        originesByDate: { ...data?.origines_by_date },
      };

      setChosenFiliationData(chosenNodesFiliationLinkedData);
    }
  }, [data, setChosenFiliationData]);

  /**
   * @description Effect hook reacting on chosenFiliationEvents in order to compute every filiation state corresponding to each events.
   * This effect will be triggered when chosenFiliationEvents changes and need to be computed without blocking the UI.
   * Main goal is to avoid re-computing the filiation data at each event selection but only render the appropriate part of the filiations by events state object (TODO).
   *
   * @dependency {FiliationEvent[]} chosenFiliationEvents - Chosen filiation events updated by the call to the API and processed to be restricted on chosen filiation before update
   */
  useEffect(() => {
    // Check if chosenFiliationEvents is different from previous chosenFiliationEvents state
    if (
      !areArraysEquals(previousChosenFiliationEvents, chosenFiliationEvents) &&
      chosenFiliationEvents &&
      chosenFiliationData?.originesByDate
    ) {
      const filiationStatesByEventsValue: FiliationEventWithFiliationStateGroupedByDate[] =
        getFiliationEventsGroupedByDates(chosenFiliationEvents, {...chosenFiliationData?.originesByDate});

      const newFiliationStatesByEvents = [...filiationStatesByEventsValue];

      setFiliationStatesByEvents([...newFiliationStatesByEvents]);
    }
  }, [chosenFiliationEvents, chosenFiliationData, setFiliationStatesByEvents, getFiliationEventsGroupedByDates]);

  /**
   * @description Callback function to handle current date change.
   */
  const handleCurrentDateChange = useCallback(
    (date: string) => {
      setCurrentDate(date);
    },
    [setCurrentDate]
  );

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  // Fonction pour télécharger le PDF de la vue d'ensemble
  const handleDownloadPdf = async () => {
    const element = document.querySelector('.react-flow') as HTMLElement;
    if (!element) return;

    // Sauvegarder les styles originaux pour les restaurer après
    const originalStyles: { element: Element; attr: string; value: string | null }[] = [];

    try {
      // Cacher la minimap et les controls
      const minimap = element.querySelector('.react-flow__minimap') as HTMLElement;
      const controls = element.querySelector('.react-flow__controls') as HTMLElement;
      if (minimap) minimap.style.display = 'none';
      if (controls) controls.style.display = 'none';

      // Forcer TOUS les edges et leurs paths à être visibles avec des attributs SVG
      const edgeContainer = element.querySelector('.react-flow__edges');
      if (edgeContainer) {
        (edgeContainer as HTMLElement).style.opacity = '1';
      }
      
      const allPaths = element.querySelectorAll('.react-flow__edge path, .react-flow__edge-path');
      allPaths.forEach((path: Element) => {
        // Sauvegarder et modifier les attributs SVG directement
        originalStyles.push({ element: path, attr: 'stroke', value: path.getAttribute('stroke') });
        originalStyles.push({ element: path, attr: 'stroke-width', value: path.getAttribute('stroke-width') });
        originalStyles.push({ element: path, attr: 'opacity', value: path.getAttribute('opacity') });
        
        path.setAttribute('stroke', '#888888');
        path.setAttribute('stroke-width', '2');
        path.setAttribute('opacity', '1');
        (path as SVGElement).style.stroke = '#888888';
        (path as SVGElement).style.strokeWidth = '2';
        (path as SVGElement).style.opacity = '1';
      });

      // Attendre un tick pour que les styles soient appliqués
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: true, // Activer les logs pour debug
        backgroundColor: "#ffffff",
        foreignObjectRendering: true, // Essayer le rendu foreignObject pour SVG
        ignoreElements: (el) => {
          return el.classList?.contains('react-flow__minimap') || 
                 el.classList?.contains('react-flow__controls');
        },
      });

      // Restaurer les styles originaux
      if (minimap) minimap.style.display = '';
      if (controls) controls.style.display = '';
      originalStyles.forEach(({ element, attr, value }) => {
        if (value !== null) {
          element.setAttribute(attr, value);
        } else {
          element.removeAttribute(attr);
        }
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      // Dimensions A4 paysage
      const pageWidth = 297;
      const pageHeight = 210;
      const margin = 10;
      
      // Calculer les dimensions de l'image en gardant le ratio
      const maxWidth = pageWidth - (margin * 2);
      const maxHeight = pageHeight - (margin * 2);
      
      let imgWidth = maxWidth;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Si l'image est trop haute, ajuster par la hauteur
      if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = (canvas.width * imgHeight) / canvas.height;
      }
      
      // Centrer l'image sur la page
      const xOffset = (pageWidth - imgWidth) / 2;
      const yOffset = (pageHeight - imgHeight) / 2;

      pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);
      pdf.save(`vue_ensemble_filiation_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error("Erreur lors de la génération du PDF:", error);
      // Restaurer les styles en cas d'erreur
      originalStyles.forEach(({ element, attr, value }) => {
        if (value !== null) {
          element.setAttribute(attr, value);
        } else {
          element.removeAttribute(attr);
        }
      });
    }
  };

  // Vérifier si le diagramme est vide (pas de formalités détectées)
  const isDiagramEmpty = !isLoading && data && (!data.origines || data.origines.length === 0);


  return (
    <Box
      sx={{
        backdropFilter: `blur(${DEFAULT_BLUR}px)`,
        backgroundColor: "rgba(255,255,255,0.2)",
        display: "flex",
        height: "100vh",
        margin: 0,
        overflow: "hidden",
        padding: 0,
        WebkitBackdropFilter: `blur(${DEFAULT_BLUR}px)`,
      }}
    >
      <CssBaseline />
      <IconButton
        aria-label="open drawer"
        onClick={handleDrawerOpen}
        edge="start"
        sx={[
          {
            position: "absolute",
            zIndex: 3000,
            left: "12px",
            top: `${TOOLBAR_HEIGHT}px`,
            "& .MuiSvgIcon-root ": {
              fill: `${BLUE_BYLAW}`,
            },
          },
          open && { display: "none" },
        ]}
      >
        <ExpandCircleDownIcon fontSize="large" sx={{ rotate: "270deg", color: `${WHITE}` }} />
      </IconButton>
      <AppBar position="fixed" open={open} variant="elevation" elevation={DEFAULT_ELEVATION}>
        <Toolbar
          disableGutters
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: `${WHITE}`,
            minHeight: `${TOOLBAR_HEIGHT}px !important`,
            px: 2,
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: `${WHITE}`,
              display: "flex",
              height: `${TOOLBAR_HEIGHT}px`,
              minWidth: "120px",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Logo height="40px" />
          </Box>
          {filiationStatesByEvents && filiationStatesByEvents.length > 0 ? (
            <TimeLinePanel
              data={filiationStatesByEvents}
              setCurrentDate={handleCurrentDateChange}
              todayData={chosenFiliationData?.nodes}
              today={today}
              currentDate={currentDate}
              onDownloadPdf={handleDownloadPdf}
            />
          ) : null}
        </Toolbar>
      </AppBar>
      <Drawer
        anchor={"left"}
        variant="persistent"
        elevation={DEFAULT_ELEVATION}
        open={open}
        hideBackdrop={true}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        PaperProps={{
          variant: "elevation",
          elevation: DEFAULT_ELEVATION,
          sx: {
            borderRadius: BORDER_RADIUS_MUI,
            top: `${TOOLBAR_HEIGHT + DEFAULT_PADDING}px`,
            left: `${DEFAULT_PADDING}px`,
            bottom: `${DEFAULT_PADDING}px`,
            height: "unset",
            width: `${PANEL_WIDTH}px`,
          },
        }}
      >
        <FiliationPanel
          setSelectedProperties={setSelectedProperties}
          selectedProperties={selectedProperties}
          filteredFiliationDataIds={chosenFiliationData?.nodes.map((node: any) => node.id)}
          parentLoading={isLoading}
          handleDrawerClose={handleDrawerClose}
        />
      </Drawer>
      <Main open={open}>
        {isDiagramEmpty ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              padding: 4,
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                maxWidth: "450px",
                padding: 3,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: 2,
                boxShadow: 2,
              }}
            >
              <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                Aucun diagramme n'est encore disponible
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Nous n'avons pas encore détecté de formalité permettant la création du diagramme (ex : division parcellaire, état descriptif de division, etc …)
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Si vous venez de sélectionner le ou les biens objet de votre dossier, veuillez patienter, l'analyse est en cours.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.close()}
                sx={{
                  borderRadius: "10px",
                }}
              >
                Compris
              </Button>
            </Box>
          </Box>
        ) : (
          <ReactFlowProvider>
            {currentDate !== "overview" && <FiliationLegend drawerOpen={open} />}
            <Flow
              chosenFiliationData={chosenFiliationData}
              currentDate={currentDate}
              filiationStatesByEvents={filiationStatesByEvents}
              panelOpenState={open}
              previousChosenFiliationData={previousChosenFiliationData}
              previousCurrentDate={previousCurrentDate}
              overviewData={data ? { 
                  nodes: data.nodes, 
                  origines: data.origines, 
                  origines_by_date: data.origines_by_date, 
                  cadastre_filiation_diagramme: data.cadastre_filiation_diagramme,
                  fullData: data 
                } : null}
            />
          </ReactFlowProvider>
        )}
      </Main>
    </Box>
  );
};

export default FiliationDiagramme;
